import {ReportFilterOptionsEnum} from '../../../analytics/helpers/analyticsReportHelpers';
import {mapFiltersToTerms} from '../../../analytics/helpers/analyticsSavedViewsHelpers';
import {AnalyticsMetricsKeys} from '../../../models/api/analyticsMetricsModel';
import {
  AnalyticsReportFilterOptions,
  AnalyticsSavedViewFiltersModel,
} from '../../../models/api/analyticsSavedViewModel';
import {AppState} from '../../appState';
import {makeGetTimezone} from '../../data/schedulingLinks/schedulingLinksSelectors';
import {ANALYTICS_ROOM_NAME_PREFIX} from './analyticsPanelSelectors';
import {getAnalyticsDetailsSelectedMetricWithOptions} from './details/analyticsPanelDetailsSelectors';
import {getAnalyticsFilters, getAnalyticsFilterTagMode} from './filters/analyticsPanelFiltersSelectors';
import {
  getAnalyticsDatetimes,
  getAnalyticsSelectedViewId,
} from './parameters/analyticsPanelParametersSelectors';
import {getAnalyticsViews} from './views/analyticsPanelViewsSelectors';
import {isAnalyticsViewSaved} from './views/analyticsPanelViewsState';

// Build body query to fetch report data (takes an array of AnalyticsMetricsKeys) or metric details (takes an AnalyticsScalarMetricsEnum)
export function buildAnalyticsQueryFromState(
  state: AppState,
  metricBody: {metric: AnalyticsMetricsKeys} | {metrics: ReadonlyArray<AnalyticsMetricsKeys>},
  options: AnalyticsReportFilterOptions = {},
) {
  const filters = {terms: mapFiltersToTerms(getAnalyticsFilters(state))};
  const [startDate, endDate] = getAnalyticsDatetimes(state);
  const tz = makeGetTimezone()(state);
  const queryOptions = attachTagModeToOptions(state, options);

  return {
    ...metricBody,
    period: {
      start: startDate,
      end: endDate,
      tz,
    },
    filters: {
      ...filters,
      options: {
        ...queryOptions,
        // We include the saved view ID (if any) to enrich backend data events.
        saved_view_id: getSavedViewId(state),
      },
    },
  };
}

// We always need to manually attach the Tag mode to the options before querying a report, metric details or saving a view. This helper does just that.
export function attachTagModeToOptions(state: AppState, options: AnalyticsReportFilterOptions) {
  const tagMode = getAnalyticsFilterTagMode(state);

  // Attaching the TagMode option (currently tagged / has been tagged).
  return {
    ...options,
    [ReportFilterOptionsEnum.TAG_MODE]: tagMode,
  };
}

// If there are views and the selected on is saved, return its ID, otherwise null.
function getSavedViewId(state: AppState) {
  if (getAnalyticsViews(state) === undefined) {
    return null;
  }

  const selectedViewId = getAnalyticsSelectedViewId(state);

  if (!isAnalyticsViewSaved(selectedViewId)) {
    return null;
  }

  return selectedViewId;
}

export function isPusherRoomAnalytics(room: string) {
  return room.startsWith(ANALYTICS_ROOM_NAME_PREFIX);
}

export function buildMetricDetailsFilters(state: AppState, filters: AnalyticsSavedViewFiltersModel) {
  const {options} = getAnalyticsDetailsSelectedMetricWithOptions(state);
  const queryOptions = attachTagModeToOptions(
    state,
    options ?? {
      [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: true,
    },
  );
  return {
    ...filters,
    options: {
      ...queryOptions,
    },
  };
}

// Main API entry path for the analytics.
// We're using /anltcs to avoid being blocked by adblockers. See #8294.
export function makeAnalyticsApiPath(companyUrl: string, selectedNamespace?: string) {
  return selectedNamespace ? `${companyUrl}/namespaces/${selectedNamespace}/anltcs` : `${companyUrl}/anltcs`;
}
